import { FC, useEffect, useMemo, useState } from "react";
import { handleComponentClick } from "./styledTreeItem";
import { Box, Typography } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { Colors } from "../../../configs";
import StyledTreeItem from "./styledTreeItem";
import CustomDateTimePicker from "../custom-date-time-picker/custom-date-time-picker";
import getCheckOutDate from "../../../configs/checkoutDateRules";
import { formatCurrency } from "../../../util/common-functions";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { IStoreConfigResponse } from "../../../typings/interfaces/responses/store-config-response";
import { useSpecialDate } from "../../../hooks/useSpecialDate";

export interface ITreeParentNodeProps {
  children?: React.ReactNode;
  nodeID: string;
  service: string;
  special?: boolean;
  specialDate: Dayjs | null;
  specialTime: Dayjs | null;
  qty: number;
  total: number;
  onChangeSpecial(): void;
  onChangeSpecialDate(date: Dayjs | null): void;
  onChangeSpecialTime(date: Dayjs | null): void;
  masterSpecial: boolean;
  onStatusClick?: () => void;
  statusColor?: string;
}

const TreeParentNode: FC<ITreeParentNodeProps> = ({
  children,
  nodeID,
  service,
  special = false,
  specialDate,
  specialTime,
  qty,
  total,
  onChangeSpecial,
  onChangeSpecialDate,
  onChangeSpecialTime,
  masterSpecial,
  onStatusClick,
  statusColor,
}) => {
  const dispatch = useAppDispatch();
  const getStoreConfigResponse = useAppSelector(
    (state) => state.store.getStoreConfigResponse
  );

  const { getColorPintags } = useAppSelector((state) => state.pintag);
  const isHex = (v: string) => /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v);

  const palette = (getColorPintags?.body ?? []) as Array<{
    alias?: string;
    description?: string;
    color?: string;
  }>;

  const resolvedStatusColor = useMemo(() => {
    if (!statusColor) return "transparent";
    const v = String(statusColor).trim();
    if (isHex(v)) return v;

    const key = v.toLowerCase();
    const match = palette.find(
      (p) =>
        (p.alias && p.alias.toLowerCase() === key) ||
        (p.description && p.description.toLowerCase() === key)
    );
    return match?.color ?? "transparent";
  }, [statusColor, palette]);

  // const [specialDateValue, setSpecialDateValue] = useState<Dayjs>(specialDate);
  // const [specialTimeValue, setSpecialTimeValue] = useState<Dayjs>(specialTime);

  // useEffect(() => {
  //   setSpecialDateValue(specialDate);
  //   setSpecialTimeValue(specialTime);
  // }, [specialDate, specialTime]);

  const handleOnChangeSpecialDate = (date: Dayjs | null) => {
    let nxtDate;
    const checkInCutOffTimeString =
      getStoreConfigResponse?.body?.cutOffTime || "10:00:00";
    const pickupTimeString =
      getStoreConfigResponse?.body?.pickupTime || "15:00:00";

    const [
      checkInCutOffTimeHours,
      checkInCutOffTimeMinutes,
      checkInCutOffTimeSeconds,
    ] = checkInCutOffTimeString.split(":").map(Number);
    const checkInCutOffTimeDate = new Date();
    checkInCutOffTimeDate.setHours(
      checkInCutOffTimeHours,
      checkInCutOffTimeMinutes,
      checkInCutOffTimeSeconds,
      0
    );

    const [pickupTimeHours, pickupTimeMinutes, pickupTimeSeconds] =
      pickupTimeString.split(":").map(Number);
    const pickupTimeDate = new Date();
    pickupTimeDate.setHours(
      pickupTimeHours,
      pickupTimeMinutes,
      pickupTimeSeconds,
      0
    );

    if (date === null) {
      nxtDate = getCheckOutDate({
        isSpecialDay: special,
        checkInCutOffTime: checkInCutOffTimeDate,
        pickupTime: pickupTimeDate,
        workingDays: getStoreConfigResponse?.body?.workingDays ?? [],
        generalServiceDays: getStoreConfigResponse?.body?.daysToDeliver ?? 2,
        specialServiceDays:
          getStoreConfigResponse?.body?.specialDaysToDeliver ?? 0,
      });
    } else {
      nxtDate = date;
    }
    // setSpecialDateValue(nxtDate);
    onChangeSpecialDate(nxtDate);
  };
  const handleOnChangeSpecialTime = (time: Dayjs | null) => {
    let nxtTime;
    const checkInCutOffTimeString =
      getStoreConfigResponse?.body?.cutOffTime || "10:00:00";
    const pickupTimeString =
      getStoreConfigResponse?.body?.pickupTime || "15:00:00";

    const [
      checkInCutOffTimeHours,
      checkInCutOffTimeMinutes,
      checkInCutOffTimeSeconds,
    ] = checkInCutOffTimeString.split(":").map(Number);
    const checkInCutOffTimeDate = new Date();
    checkInCutOffTimeDate.setHours(
      checkInCutOffTimeHours,
      checkInCutOffTimeMinutes,
      checkInCutOffTimeSeconds,
      0
    );

    const [pickupTimeHours, pickupTimeMinutes, pickupTimeSeconds] =
      pickupTimeString.split(":").map(Number);
    const pickupTimeDate = new Date();
    pickupTimeDate.setHours(
      pickupTimeHours,
      pickupTimeMinutes,
      pickupTimeSeconds,
      0
    );
    if (time === null) {
      nxtTime = dayjs(
        getCheckOutDate({
          isSpecialDay: special,
          checkInCutOffTime: checkInCutOffTimeDate,
          pickupTime: pickupTimeDate,
          workingDays: getStoreConfigResponse?.body?.workingDays ?? [],
          generalServiceDays: getStoreConfigResponse?.body?.daysToDeliver ?? 2,
          specialServiceDays:
            getStoreConfigResponse?.body?.specialDaysToDeliver ?? 0,
        })
      );
    } else {
      nxtTime = time;
    }
    // setSpecialTimeValue(nxtTime);
    onChangeSpecialTime(nxtTime);
  };

  useEffect(() => {
    const checkInCutOffTimeString =
      getStoreConfigResponse?.body?.cutOffTime || "10:00:00";
    const pickupTimeString =
      getStoreConfigResponse?.body?.pickupTime || "15:00:00";

    const [
      checkInCutOffTimeHours,
      checkInCutOffTimeMinutes,
      checkInCutOffTimeSeconds,
    ] = checkInCutOffTimeString.split(":").map(Number);
    const checkInCutOffTimeDate = new Date();
    checkInCutOffTimeDate.setHours(
      checkInCutOffTimeHours,
      checkInCutOffTimeMinutes,
      checkInCutOffTimeSeconds,
      0
    );

    const [pickupTimeHours, pickupTimeMinutes, pickupTimeSeconds] =
      pickupTimeString.split(":").map(Number);
    const pickupTimeDate = new Date();
    pickupTimeDate.setHours(
      pickupTimeHours,
      pickupTimeMinutes,
      pickupTimeSeconds,
      0
    );
    handleOnChangeSpecialDate(
      getCheckOutDate({
        isSpecialDay: special,
        checkInCutOffTime: checkInCutOffTimeDate,
        pickupTime: pickupTimeDate,
        workingDays: getStoreConfigResponse?.body?.workingDays ?? [],
        generalServiceDays: getStoreConfigResponse?.body?.daysToDeliver ?? 2,
        specialServiceDays:
          getStoreConfigResponse?.body?.specialDaysToDeliver ?? 0,
      })
    );
  }, [special]);

  const { date: NewspecialDate } = useSpecialDate();

  return (
    <StyledTreeItem
      nodeId={nodeID}
      label={
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            //gap: "1rem",
            //justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              fontSize: "1.14285714rem",
              lineHeight: "1.37142857rem",
              color: Colors.BLACK,
              fontWeight: "bold",
              marginRight: "auto",
              width: "18rem",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {service}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              //gap: "0.714285714rem",
              gap: "1rem",
              alignItems: "center",
              //mt: "0.07rem",
            }}
            onClick={handleComponentClick}
          >
            {/* The Special switch is hidden: Special / Regular comes from
                the ticket's pin tag (the tab picked in the pin tag modal) */}
            <CustomDateTimePicker
              type="date"
              value={special ? NewspecialDate : specialDate}
              onChange={handleOnChangeSpecialDate}
              disabled={true}
            />
            <CustomDateTimePicker
              type="time"
              value={specialTime}
              onChange={handleOnChangeSpecialTime}
              // disabled={masterSpecial && special}
              disabled={true}
            />
          </Box>

          <Box
            role="button"
            aria-label={`${service} pin tag`}
            onClick={(e) => {
              // Only the dot opens the pin tag drawer; don't toggle the row
              e.stopPropagation();
              onStatusClick?.();
            }}
            sx={{
              width: "1.7rem",
              height: "1.7rem",
              backgroundColor: resolvedStatusColor || "transparent",
              borderRadius: "50%",
              marginLeft: "2rem",
              marginRight: "2rem",
              border: "0.05rem solid rgba(0, 0, 0, 0.1)",
              cursor: "pointer",
            }}
          />
          <Typography
            sx={{
              fontSize: "1.42857143rem",
              lineHeight: "1.71428571rem",
              color: Colors.TEXT_GRAY_DARK,
              // marginRight: "-5.1rem",
              marginRight: "4.7rem",
              marginLeft: "auto",
            }}
          >
            x{qty}
          </Typography>
          <Typography
            sx={{
              fontSize: "1.42857143rem",
              lineHeight: "1.71428571rem",
              color: Colors.TEXT_GRAY_DARK,
              width: "8.5rem",
              textAlign: "right",
            }}
          >
            {formatCurrency({
              amount: total,
              prefix: "$",
            })}
          </Typography>
        </Box>
      }
      type="parent"
      sx={{
        "& > .MuiTreeItem-content": {
          background: Colors.BACKGROUND_GRAY,
          width: "100%",
          height: "3.85714286rem",
          borderRadius: "0.428571429rem",
          display: "flex",
          alignItems: "center",
          padding: "1.07142857rem",
          border: `0.0714285714rem solid ${Colors.ACCENT_GRAY_DISABLED}`,
          "&:hover": {
            background: Colors.ACCENT_GRAY,
          },
        },
        "&[aria-expanded=true]>div:nth-of-type(2)": {
          marginBottom: "3.07142857rem",
        },
        "&[aria-expanded=false]>div:nth-of-type(2)": {
          marginBottom: "0.9998rem",
        },
      }}
    >
      {children}
    </StyledTreeItem>
  );
};

export default TreeParentNode;
